import { useCallback, useEffect, useRef, useState } from 'react'
import { callableRef } from '../../../../api/callableRef'
import useChatAssistant, {
  ChatMessage,
  ToolResultEventPayload,
} from '../../../../hooks/useChatAssistant'
import { useApplicationSuiteContext } from '../../../../contexts/ApplicationSuiteProvider'
import { useCopilotStatus } from '../contexts/PatternCopilotContext'
import { CopilotDraftHost, DraftEnvelope } from '../types'
import { composeTurnMessage, isDraftEnvelope } from '../utils/draftEnvelope'
import { revealCondition } from '../utils/revealCondition'
import { captureEvent } from '../../../../posthog'

const CONVERSATION_MAP_KEY = 'copilotConversations'
const CONVERSATION_MAP_MAX_ENTRIES = 100

export interface DraftCardInfo {
  status: 'applied' | 'held' | 'rejected' | 'superseded' | 'discarded'
  summary: string
  diffSummary?: string
  firstAddedConditionId?: string
  warnings: string[]
  detail?: string
}

const readConversationMap = (): Record<string, string> => {
  try {
    return JSON.parse(localStorage.getItem(CONVERSATION_MAP_KEY) ?? '{}')
  } catch {
    return {}
  }
}

const writeConversationMap = (map: Record<string, string>) => {
  // The map gains one entry per entity ever chatted and only reset() deletes;
  // trim oldest-inserted entries so it can't grow unbounded in localStorage
  const keys = Object.keys(map)
  for (const key of keys.slice(0, Math.max(0, keys.length - CONVERSATION_MAP_MAX_ENTRIES))) {
    delete map[key]
  }
  localStorage.setItem(CONVERSATION_MAP_KEY, JSON.stringify(map))
}

const scrollToCondition = (conditionId: string) => {
  setTimeout(() => {
    revealCondition(conditionId)
  }, 150)
}

/**
 * Orchestrates one copilot session over a CopilotDraftHost: composes each turn
 * with the live draft (sentinel lane — zero backend changes), receives
 * DraftEnvelopes from tool_result payloads (feature-detected: without a
 * draft-capable backend the panel is a plain advisor chat), coalesces bursts,
 * and drives the host's apply pipeline.
 *
 * The draft is attached on EVERY turn. A changed-since-last-send gate was
 * considered and rejected: reset, revert, and undo all make "what the agent
 * last saw" diverge from "what was last sent", and a stale gate silently
 * starves the agent of the true state — token cost is the safer trade.
 */
export default function useCopilotSession(host: CopilotDraftHost) {
  const { tradingHouse } = useApplicationSuiteContext()
  const { setStatus } = useCopilotStatus()

  const [draftCards, setDraftCards] = useState<Record<string, DraftCardInfo>>({})
  const turnStartRevisionRef = useRef(0)
  const pendingEnvelopesRef = useRef<Array<{ envelope: DraftEnvelope; toolCallId: string }>>([])
  const coalesceScheduledRef = useRef(false)
  const hostRef = useRef(host)
  hostRef.current = host

  // Conversations are stored per entity, namespaced by type so a pattern and
  // an indicator with colliding ids never share one
  const mapKey = host.entityId ? `${host.entityType}:${host.entityId}` : null

  // Lazy read once per mount (the session is keyed per entity); refreshed
  // explicitly on reset
  const [storedConversationId, setStoredConversationId] = useState<string | undefined>(() =>
    mapKey ? readConversationMap()[mapKey] : undefined
  )
  // Resume is the one async path outside the stream — surface its failures
  // through the same panel error Alert instead of a silent console line
  const [resumeError, setResumeError] = useState<string | null>(null)
  const [isResuming, setIsResuming] = useState(false)

  const cardFromApplyResult = useCallback(
    (
      envelope: DraftEnvelope,
      result: ReturnType<CopilotDraftHost['applyDraft']>
    ): DraftCardInfo => {
      if (result.status === 'applied') {
        return {
          status: 'applied',
          summary: envelope.summary ?? 'Applied to the open pattern',
          diffSummary: result.diff.summary,
          firstAddedConditionId: result.diff.addedConditionIds[0],
          warnings: result.validationWarnings,
        }
      }
      if (result.status === 'held') {
        return { status: 'held', summary: envelope.summary ?? 'Draft ready', warnings: [] }
      }
      return {
        status: 'rejected',
        summary: envelope.summary ?? 'Draft could not be applied',
        detail: result.detail ?? result.reason,
        warnings: [],
      }
    },
    []
  )

  const flushEnvelopes = useCallback(() => {
    coalesceScheduledRef.current = false
    const batch = pendingEnvelopesRef.current
    pendingEnvelopesRef.current = []
    if (batch.length === 0) return

    // Newest wins within a burst; earlier envelopes are marked superseded
    const cards: Record<string, DraftCardInfo> = {}
    for (const { envelope, toolCallId } of batch.slice(0, -1)) {
      cards[toolCallId] = {
        status: 'superseded',
        summary: envelope.summary ?? 'Draft superseded by a newer one in the same turn',
        warnings: [],
      }
    }
    const { envelope, toolCallId } = batch[batch.length - 1]
    const result = hostRef.current.applyDraft(envelope, {
      toolCallId,
      turnStartRevision: turnStartRevisionRef.current,
    })
    cards[toolCallId] = cardFromApplyResult(envelope, result)
    captureEvent('copilot_draft_resolved', {
      entity_type: hostRef.current.entityType,
      outcome: result.status,
      superseded_count: batch.length - 1,
    })
    if (result.status === 'applied' && result.diff.addedConditionIds[0]) {
      scrollToCondition(result.diff.addedConditionIds[0])
    }
    setDraftCards((prev) => ({ ...prev, ...cards }))
  }, [cardFromApplyResult])

  const onToolResult = useCallback(
    (payload: ToolResultEventPayload) => {
      if (payload.data === undefined || payload.data === null) return
      if (!isDraftEnvelope(payload.data)) {
        // Two malformed-draft shapes get a visible error instead of a silent
        // drop: objects claiming the discriminant, and strings containing it
        // (the backend registry stringifies+truncates oversized results)
        const claimsObject =
          typeof payload.data === 'object' && (payload.data as any).kind === 'gc.draft'
        const truncatedString =
          typeof payload.data === 'string' && payload.data.includes('gc.draft')
        if (claimsObject || truncatedString) {
          setDraftCards((prev) => ({
            ...prev,
            [payload.toolCallId]: {
              status: 'rejected',
              summary: truncatedString
                ? 'Draft was truncated in transit'
                : 'Malformed draft from the assistant',
              detail: truncatedString
                ? 'The draft exceeded the transport size limit and was not applied.'
                : 'The draft payload failed validation and was not applied.',
              warnings: [],
            },
          }))
        }
        return
      }
      pendingEnvelopesRef.current.push({
        envelope: payload.data,
        toolCallId: payload.toolCallId,
      })
      if (!coalesceScheduledRef.current) {
        coalesceScheduledRef.current = true
        queueMicrotask(flushEnvelopes)
      }
    },
    [flushEnvelopes]
  )

  // The backend chat context only understands patterns today; indicator
  // sessions carry their state solely via the sentinel draft block
  const chat = useChatAssistant(
    tradingHouse as 'gtf' | 'greenchart',
    host.entityType === 'pattern' && host.entityId ? { patternId: host.entityId } : undefined,
    { segmented: true, onToolResult }
  )

  // Panel status mirrors streaming (VersionTab's auto-apply is guarded on it)
  useEffect(() => {
    setStatus(chat.isStreaming ? 'streaming' : 'idle')
    return () => setStatus('idle')
  }, [chat.isStreaming, setStatus])

  // Persist entity → conversationId so sessions survive a refresh
  useEffect(() => {
    if (!chat.conversationId || !mapKey) return
    const map = readConversationMap()
    if (map[mapKey] !== chat.conversationId) {
      map[mapKey] = chat.conversationId
      writeConversationMap(map)
    }
  }, [chat.conversationId, mapKey])

  const canResume = !!storedConversationId && chat.messages.length === 0 && !chat.conversationId

  const resume = useCallback(async () => {
    if (!storedConversationId || isResuming) return
    setResumeError(null)
    setIsResuming(true)
    try {
      const getConversation = callableRef('getChatAssistantConversation')
      const result: any = await getConversation({ conversationId: storedConversationId })
      const loaded = (result?.data?.data?.messages ?? result?.data?.messages ?? []) as any[]
      const mapped: ChatMessage[] = loaded
        .filter(
          (m) => m && typeof m.content === 'string' && (m.role === 'user' || m.role === 'assistant')
        )
        .map((m) => ({ role: m.role, content: m.content }))
      chat.loadConversation(mapped, storedConversationId)
    } catch (error) {
      console.error('Failed to resume copilot conversation:', error)
      setResumeError('Could not resume the previous conversation — start a new message below.')
    } finally {
      setIsResuming(false)
    }
  }, [storedConversationId, chat, isResuming])

  // Full reset: chat state, transcript cards, and the stored conversation
  // mapping — otherwise "New conversation" immediately offers to resume the
  // conversation that was just discarded
  const reset = useCallback(() => {
    chat.resetConversation()
    setDraftCards({})
    setResumeError(null)
    const key = hostRef.current.entityId
      ? `${hostRef.current.entityType}:${hostRef.current.entityId}`
      : null
    if (key) {
      const map = readConversationMap()
      delete map[key]
      writeConversationMap(map)
    }
    setStoredConversationId(undefined)
  }, [chat])

  const send = useCallback(
    (text: string) => {
      const trimmed = text.trim()
      // A send during resume would be clobbered when loadConversation lands
      if (!trimmed || chat.isStreaming || isResuming) return
      setResumeError(null)
      const turnContext = hostRef.current.getTurnContext()
      turnStartRevisionRef.current = turnContext.revision
      chat.sendMessage(composeTurnMessage(trimmed, turnContext, true))
    },
    [chat, isResuming]
  )

  const resolvePending = useCallback((action: 'apply' | 'discard') => {
    const outcome = hostRef.current.resolvePendingDraft(action)
    if (outcome) {
      captureEvent('copilot_draft_resolved', {
        entity_type: hostRef.current.entityType,
        outcome: outcome.result === null ? 'discarded' : outcome.result.status,
        after_hold: true,
      })
    }
    if (outcome && outcome.result === null) {
      // Discarded: the held card must not read "Draft ready" forever
      setDraftCards((prev) => ({
        ...prev,
        [outcome.toolCallId]: {
          status: 'discarded',
          summary: 'Discarded — you kept your edits',
          warnings: [],
        },
      }))
      return
    }
    if (outcome && outcome.result) {
      const card: DraftCardInfo =
        outcome.result.status === 'applied'
          ? {
              status: 'applied',
              summary: 'Applied after your edits',
              diffSummary: outcome.result.diff.summary,
              firstAddedConditionId: outcome.result.diff.addedConditionIds[0],
              warnings: outcome.result.validationWarnings,
            }
          : {
              status: 'rejected',
              summary: 'Draft could not be applied',
              detail:
                outcome.result.status === 'rejected'
                  ? (outcome.result.detail ?? outcome.result.reason)
                  : undefined,
              warnings: [],
            }
      setDraftCards((prev) => ({ ...prev, [outcome.toolCallId]: card }))
    }
  }, [])

  return {
    messages: chat.messages,
    isStreaming: chat.isStreaming,
    error: chat.error ?? resumeError,
    activeToolCalls: chat.activeToolCalls,
    send,
    stop: chat.cancel,
    reset,
    draftCards,
    canResume,
    resume,
    isResuming,
    resolvePending,
  }
}

export type CopilotSession = ReturnType<typeof useCopilotSession>
